import { useEffect, useRef } from "react";
import { useSerializeCallback } from "@kalodata/tools";
import { queryAssetProductInfo, type AssetProductInfoResponse } from "@apis/http/api";

export interface AssetProductFields {
  productId: string;
  productTitle: string;
  productImage: string;
  salesCount?: number;
}

export const productFieldsFromResponse = (info?: AssetProductInfoResponse | null): AssetProductFields => ({
  productId: info?.productId ?? "",
  productTitle: info?.productTitle ?? "",
  productImage: info?.imageUrl ?? "",
  salesCount: info?.salesCount,
});

export interface UseAssetProductInfoOptions {
  assetId: number;
  linkId: number | undefined;
  /** modal 是否处于打开状态；关闭时重置「已加载」标记 */
  active: boolean;
  /** 是否应在 linkId 变化时取数（如发布轮询视图下应暂停取数，但不重置标记） */
  enabled: boolean;
  onLoaded: (fields: AssetProductFields, ctx: { isAccountSwitch: boolean }) => void;
}

/**
 * 按 (assetId, linkId) 拉取关联商品信息：
 * - 首次加载（新 asset）视为全新会话；
 * - 同 asset 下切换 linkId 视为「切换账号」，调用方可据此保留用户已填写字段；
 * - active=false（modal 关闭）时重置标记，enabled=false（如轮询视图）时暂停但保留标记。
 */
export const useAssetProductInfo = ({ assetId, linkId, active, enabled, onLoaded }: UseAssetProductInfoOptions) => {
  const loadedAssetRef = useRef<number | null>(null);
  const loadedLinkIdRef = useRef<number | null>(null);
  const currentAssetRef = useRef<number | null>(null);
  const currentLinkIdRef = useRef<number | null>(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = useSerializeCallback(async (targetAssetId: number, targetLinkId: number, isAccountSwitch: boolean) => {
    const res = await queryAssetProductInfo({ assetId: targetAssetId, linkId: targetLinkId });
    if (currentAssetRef.current !== targetAssetId || currentLinkIdRef.current !== targetLinkId) return;
    onLoadedRef.current(productFieldsFromResponse(res), { isAccountSwitch });
  });

  useEffect(() => {
    if (!active) {
      loadedAssetRef.current = null;
      loadedLinkIdRef.current = null;
      currentAssetRef.current = null;
      currentLinkIdRef.current = null;
      return;
    }
    currentAssetRef.current = assetId;
    currentLinkIdRef.current = linkId ?? null;
    if (!enabled || linkId === undefined) return;

    if (loadedAssetRef.current !== assetId) {
      loadedAssetRef.current = assetId;
      loadedLinkIdRef.current = linkId;
      void load(assetId, linkId, false);
      return;
    }

    if (loadedLinkIdRef.current === linkId) return;

    loadedLinkIdRef.current = linkId;
    void load(assetId, linkId, true);
  }, [assetId, linkId, active, enabled, load]);

  return { loading: load.isPending() };
};
